/**
 * Bot Detection Provider
 * React context providing bot detection state and Turnstile challenge modal
 */

import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import type {
  BotDetectionContextValue,
  BotDetectionResult,
  BotSignal,
  BehaviorState,
  MouseMovement,
  KeyboardTiming,
  ScrollEvent,
  HoverEvent,
  ClickEvent,
} from './types';
import {
  runBotDetection,
  createInitialBehaviorState,
} from './detectors';
import { createBotScoreHeader } from './encryptBotScore';
import {
  initializeBotDetection,
  setTurnstileToken as setApiTurnstileToken,
} from '@/services/api/apiUtils';

// Turnstile site key from environment
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

// Bot score threshold for requiring Turnstile challenge
const BOT_SCORE_THRESHOLD = 70;

// Create context
export const BotDetectionContext = createContext<BotDetectionContextValue | null>(null);

interface BotDetectionProviderProps {
  children: React.ReactNode;
}

/**
 * Turnstile Challenge Modal Component
 */
const TurnstileModal: React.FC<{
  isOpen: boolean;
  onSuccess: (token: string) => void;
  onClose: () => void;
}> = ({ isOpen, onSuccess, onClose }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen || !containerRef.current || !TURNSTILE_SITE_KEY) return;

    // Load Turnstile script if not already loaded
    const loadTurnstile = () => {
      return new Promise<void>((resolve) => {
        if ((window as Window & { turnstile?: unknown }).turnstile) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.onload = () => resolve();
        document.head.appendChild(script);
      });
    };

    const initWidget = async () => {
      await loadTurnstile();

      const turnstile = (window as Window & { turnstile?: {
        render: (element: HTMLElement, options: {
          sitekey: string;
          callback: (token: string) => void;
          'error-callback': () => void;
          theme: string;
        }) => string;
        remove: (widgetId: string) => void;
      } }).turnstile;

      if (!turnstile || !containerRef.current) return;

      // Render the widget
      widgetIdRef.current = turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => {
          onSuccess(token);
        },
        'error-callback': () => {
          console.error('[BotDetection] Turnstile error');
        },
        theme: 'light',
      });
    };

    initWidget();

    return () => {
      // Cleanup widget on unmount
      const turnstile = (window as Window & { turnstile?: {
        remove: (widgetId: string) => void;
      } }).turnstile;
      if (turnstile && widgetIdRef.current) {
        turnstile.remove(widgetIdRef.current);
      }
    };
  }, [isOpen, onSuccess]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '400px',
          width: '90%',
        }}
      >
        <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600 }}>
          Security Check
        </h3>
        <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '14px' }}>
          Please complete this security check to continue.
        </p>
        <div
          ref={containerRef}
          style={{ minHeight: '65px', display: 'flex', justifyContent: 'center' }}
        />
        {!TURNSTILE_SITE_KEY && (
          <p style={{ color: '#e53e3e', fontSize: '12px', marginTop: '8px' }}>
            Turnstile not configured. Please set VITE_TURNSTILE_SITE_KEY.
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Honeypot Fields Component
 * Hidden fields that bots will fill but humans won't see
 */
const HoneypotFields: React.FC<{
  onTrigger: () => void;
  onLinkClick: () => void;
}> = ({ onTrigger, onLinkClick }) => {
  return (
    <>
      {/* Hidden input honeypot - positioned off-screen but not display:none */}
      <div
        style={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          opacity: 0,
          height: 0,
          width: 0,
          overflow: 'hidden',
        }}
        aria-hidden="true"
      >
        <input
          type="text"
          name="website_url"
          tabIndex={-1}
          autoComplete="off"
          onChange={onTrigger}
        />
        <input
          type="email"
          name="email_confirm"
          tabIndex={-1}
          autoComplete="off"
          onChange={onTrigger}
        />
        {/* Hidden link honeypot */}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onLinkClick();
          }}
          tabIndex={-1}
        >
          Click here for special offer
        </a>
      </div>
    </>
  );
};

/**
 * Bot Detection Provider Component
 */
export const BotDetectionProvider: React.FC<BotDetectionProviderProps> = ({
  children,
}) => {
  const [botScore, setBotScore] = useState(0);
  const [turnstileRequired, setTurnstileRequired] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [showTurnstile, setShowTurnstile] = useState(false);

  const behaviorStateRef = useRef<BehaviorState>(createInitialBehaviorState());
  const honeypotSignalsRef = useRef<BotSignal[]>([]);
  const lastDetectionRef = useRef<BotDetectionResult | null>(null);
  const lastKeyTimeRef = useRef<number>(0);

  // Initialize API utils integration
  useEffect(() => {
    initializeBotDetection(
      async () => {
        const result = await runBotDetection(
          behaviorStateRef.current,
          honeypotSignalsRef.current
        );
        lastDetectionRef.current = result;
        setBotScore(result.score);
        return createBotScoreHeader(result);
      },
      () => setShowTurnstile(true)
    );
  }, []);

  // Sync turnstile token to API utils
  useEffect(() => {
    setApiTurnstileToken(turnstileToken);
  }, [turnstileToken]);

  // Track mouse movements
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const movement: MouseMovement = {
        x: e.clientX,
        y: e.clientY,
        timestamp: Date.now(),
      };

      // Keep only last 100 movements
      behaviorStateRef.current.mouseMovements.push(movement);
      if (behaviorStateRef.current.mouseMovements.length > 100) {
        behaviorStateRef.current.mouseMovements.shift();
      }

      // Track first interaction
      if (behaviorStateRef.current.firstInteractionTime === null) {
        behaviorStateRef.current.firstInteractionTime = Date.now();
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Track keyboard timing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const now = Date.now();
      const interval = lastKeyTimeRef.current ? now - lastKeyTimeRef.current : 0;

      const timing: KeyboardTiming = {
        key: e.key,
        timestamp: now,
        interval,
      };

      behaviorStateRef.current.keyboardTimings.push(timing);
      if (behaviorStateRef.current.keyboardTimings.length > 50) {
        behaviorStateRef.current.keyboardTimings.shift();
      }

      lastKeyTimeRef.current = now;

      // Track first interaction
      if (behaviorStateRef.current.firstInteractionTime === null) {
        behaviorStateRef.current.firstInteractionTime = Date.now();
      }
    };

    window.addEventListener('keydown', handleKeyDown, { passive: true });
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Track scroll events
  useEffect(() => {
    const handleScroll = () => {
      const scrollEvent: ScrollEvent = {
        timestamp: Date.now(),
        scrollY: window.scrollY,
        scrollX: window.scrollX,
      };

      behaviorStateRef.current.scrollEvents.push(scrollEvent);
      // Keep last 50 scroll events
      if (behaviorStateRef.current.scrollEvents.length > 50) {
        behaviorStateRef.current.scrollEvents.shift();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track hover events on interactive elements
  useEffect(() => {
    let hoverStartTime: number | null = null;
    let currentElement: Element | null = null;

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as Element;
      if (target.matches && target.matches('button, a, input, textarea, select, [role="button"]')) {
        hoverStartTime = Date.now();
        currentElement = target;
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      if (hoverStartTime && currentElement) {
        const hoverEvent: HoverEvent = {
          timestamp: hoverStartTime,
          elementId: (currentElement as HTMLElement).id || null,
          duration: Date.now() - hoverStartTime,
        };

        behaviorStateRef.current.hoverEvents.push(hoverEvent);
        // Keep last 50 hover events
        if (behaviorStateRef.current.hoverEvents.length > 50) {
          behaviorStateRef.current.hoverEvents.shift();
        }

        hoverStartTime = null;
        currentElement = null;
      }
    };

    document.addEventListener('mouseover', handleMouseOver, { passive: true });
    document.addEventListener('mouseout', handleMouseOut, { passive: true });
    return () => {
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, []);

  // Track clicks with context (prior hover and mouse movement)
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const now = Date.now();

      // Check for recent mouse movements (at least 5 in last second)
      const recentMoves = behaviorStateRef.current.mouseMovements.filter(
        m => now - m.timestamp < 1000
      );

      // Check for recent hovers (in last 2 seconds)
      const recentHovers = behaviorStateRef.current.hoverEvents.filter(
        h => now - h.timestamp < 2000
      );

      const clickEvent: ClickEvent = {
        timestamp: now,
        x: e.clientX,
        y: e.clientY,
        hadPriorHover: recentHovers.length > 0,
        hadPriorMouseMove: recentMoves.length >= 5,
      };

      behaviorStateRef.current.clickEvents.push(clickEvent);
      // Keep last 50 click events
      if (behaviorStateRef.current.clickEvents.length > 50) {
        behaviorStateRef.current.clickEvents.shift();
      }
    };

    document.addEventListener('click', handleClick, { passive: true });
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Track form fill timing
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as Element;
      if (target.matches && target.matches('input, textarea, select')) {
        // Only set start time on first form field focus
        if (!behaviorStateRef.current.formFillStartTime) {
          behaviorStateRef.current.formFillStartTime = Date.now();
        }
      }
    };

    const handleBlur = (e: FocusEvent) => {
      const target = e.target as Element;
      if (target.matches && target.matches('input, textarea, select')) {
        // Update end time on every blur (last one wins)
        behaviorStateRef.current.formFillEndTime = Date.now();
      }
    };

    document.addEventListener('focusin', handleFocus, { passive: true });
    document.addEventListener('focusout', handleBlur, { passive: true });
    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, []);

  // Run detection periodically
  useEffect(() => {
    const runDetection = async () => {
      const result = await runBotDetection(
        behaviorStateRef.current,
        honeypotSignalsRef.current
      );
      lastDetectionRef.current = result;
      setBotScore(result.score);

      // Check if Turnstile is required
      if (result.score >= BOT_SCORE_THRESHOLD && !turnstileToken) {
        setTurnstileRequired(true);
      }
    };

    // Run initial detection after a short delay
    const initialTimeout = setTimeout(runDetection, 2000);

    // Run periodic detection every 30 seconds
    const interval = setInterval(runDetection, 30000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [turnstileToken]);

  // Get encrypted bot score for API requests
  const getEncryptedBotScore = useCallback(async (): Promise<string> => {
    // Run fresh detection
    const result = await runBotDetection(
      behaviorStateRef.current,
      honeypotSignalsRef.current
    );
    lastDetectionRef.current = result;
    setBotScore(result.score);

    // Check if Turnstile is required based on bot score
    if (result.score >= BOT_SCORE_THRESHOLD && !turnstileToken) {
      setTurnstileRequired(true);
      setShowTurnstile(true);
      console.warn('[BotDetection] High bot score detected:', result.score, 'Signals:', result.signals);
    }

    return createBotScoreHeader(result);
  }, [turnstileToken]);

  // Show Turnstile challenge
  const showTurnstileChallenge = useCallback(() => {
    setShowTurnstile(true);
  }, []);

  // Handle Turnstile success
  const onTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token);
    setTurnstileRequired(false);
    setShowTurnstile(false);
  }, []);

  // Report honeypot trigger
  const reportHoneypotTrigger = useCallback(() => {
    if (!honeypotSignalsRef.current.includes('honeypot_triggered')) {
      honeypotSignalsRef.current.push('honeypot_triggered');
      console.warn('[BotDetection] Honeypot field triggered');
    }
  }, []);

  // Report honeypot link click
  const reportHoneypotLinkClick = useCallback(() => {
    if (!honeypotSignalsRef.current.includes('honeypot_link_clicked')) {
      honeypotSignalsRef.current.push('honeypot_link_clicked');
      console.warn('[BotDetection] Honeypot link clicked');
    }
  }, []);

  const contextValue: BotDetectionContextValue = {
    botScore,
    turnstileRequired,
    turnstileToken,
    getEncryptedBotScore,
    showTurnstileChallenge,
    onTurnstileSuccess,
    reportHoneypotTrigger,
    reportHoneypotLinkClick,
  };

  return (
    <BotDetectionContext.Provider value={contextValue}>
      {children}
      <HoneypotFields
        onTrigger={reportHoneypotTrigger}
        onLinkClick={reportHoneypotLinkClick}
      />
      <TurnstileModal
        isOpen={showTurnstile}
        onSuccess={onTurnstileSuccess}
        onClose={() => setShowTurnstile(false)}
      />
    </BotDetectionContext.Provider>
  );
};

export default BotDetectionProvider;
